/**
 * The island: landing, state and moving.
 *
 * The state response carries only what the player may see -- their own seen
 * tiles and the hearsay they have charted -- never the full map. Everything
 * about a move is decided by the server (`AGENTS.md`, server authority); the
 * client sends a direction and is told what happened.
 */

import { z } from 'zod'
import {
  budgetSchema,
  dayStatusSchema,
  directionSchema,
  gameDaySchema,
  islandNameSchema,
  logLineSchema,
  mapCellSchema,
  positionSchema,
} from './common.ts'

/** A relic tile the player knows about, by its index on this island. */
export const relicMarkerSchema = z.object({
  /** Index into the island's relic list; what a stake is keyed by. */
  index: z.number().int().nonnegative(),
  at: positionSchema,
  /** The catalogue id, once the player has stood on it. */
  relicId: z.string().optional(),
  /** Somebody has staked it. Shown as the hollow glyph (Q3.2). */
  spokenFor: z.boolean(),
  /** This player has staked it. */
  staked: z.boolean(),
})

/**
 * A landing shore, by its index on this island.
 *
 * The rising day draws one of these for a player; the sinking day lets them
 * choose (Q21.1). The index is what a return landing names, so the client
 * sends a number the server can check rather than a position it could invent.
 */
export const shoreSchema = z.object({
  index: z.number().int().nonnegative(),
  at: positionSchema,
})

export const islandStateResponseSchema = z.object({
  day: gameDaySchema,
  /** Counts from `GAME_EPOCH`; null for the unnumbered test islands (Q1.3). */
  dayNumber: z.number().int().positive().nullable(),
  /** The name as a structure, rendered by the client in its own locale. */
  islandName: islandNameSchema,
  status: dayStatusSchema,
  budget: budgetSchema,
  position: positionSchema,
  /** Null on replay, which has no limit (Q1.2). */
  stamina: z.number().int().nonnegative().nullable(),
  staminaTotal: z.number().int().positive().nullable(),
  /** Seen and charted tiles only. The rest of the island is not sent. */
  cells: z.array(mapCellSchema),
  relics: z.array(relicMarkerSchema),
  /**
   * The shores the return trip may land on, and empty at every other moment
   * (Q21.1).
   *
   * A shore is ground the player may not have walked, so it is sent only while
   * there is a choice to be made with it: the sinking day, before the landing.
   * Once the boat is in, the list is empty again and the map is the player's
   * own sight, as it is on every other day.
   */
  shores: z.array(shoreSchema),
  /**
   * False only while the island is still owed a landing.
   *
   * The return trip is a return: the player arrives by sea and chooses one of
   * the landing shores before their first move, once (Q21.1). True on the
   * rising day and on replay, which are not arrivals by sea, and true on a
   * sinking island the player is landing on for the first time -- that lands
   * as any other day does and asks nothing.
   */
  returnLanded: z.boolean(),
  /** Ambient narration for the screen, as an event and a seed. */
  log: logLineSchema.nullable(),
  /**
   * True when this is the player's first landing: they had never played when
   * they asked for it (Q19.1). The interface says a little more on a first
   * island and then never again. It tells the asker something about
   * themselves and nothing about anybody else.
   */
  firstLanding: z.boolean(),
  /**
   * True when no legal move remains: nothing is affordable, which is not the
   * same as nothing being left (Q20.2). A player on one point among forest is
   * as finished as one on zero, and the interface says so. What a step costs
   * is the server's arithmetic, never the client's.
   */
  spent: z.boolean(),
  /**
   * When the tide turns, as a unix timestamp in seconds (Q30.1): for a rising
   * island the moment it begins sinking, for a sinking island the moment it
   * finalises, null once it has. Both are 00:00 UTC of a day the server
   * knows, so the server computes it from the island's day and never from a
   * clock the request brought (Q17.1). The client only formats it, in the
   * viewer's own zone, and counts down to it for display.
   */
  turnsAt: z.number().int().nullable(),
  /** Published once the island has finalised, so a player may check it. */
  seedHex: z.string().optional(),
})
export type ApiIslandState = z.infer<typeof islandStateResponseSchema>

/**
 * The return landing: which shore the boat puts in at (Q21.1).
 *
 * An index into the state's `shores`, never a position, because a position is
 * a thing a client could make up and an index is a thing the server can check.
 */
export const returnLandingRequestSchema = z.object({
  shoreIndex: z.number().int().nonnegative(),
})
export type ApiReturnLanding = z.infer<typeof returnLandingRequestSchema>

/**
 * What a return landing answers with: the whole island state.
 *
 * The landing moves the player, shows them what the shore can see and closes
 * the choice, so the screen redraws from one truth rather than patching three
 * fields it worked out for itself.
 */
export const returnLandingResponseSchema = islandStateResponseSchema

/**
 * The refusal a move gets on the return budget before a shore is chosen.
 *
 * Typed, and refused on the server, because the client may not decide an
 * outcome (`AGENTS.md`): a screen that forgot to ask is told no rather than
 * quietly walking from wherever the rising day left off.
 */
export const CHOOSE_A_SHORE = 'choose-a-shore'

export const moveRequestSchema = z.object({ direction: directionSchema })

/** What one step did. A refusal is an `apiError` with its own log event. */
export const moveResponseSchema = z.object({
  position: positionSchema,
  stamina: z.number().int().nonnegative().nullable(),
  /** Tiles this move showed for the first time. */
  revealed: z.array(mapCellSchema),
  /**
   * Every relic this move brought into sight, in the state's own shape and by
   * the state's own rule (#129).
   *
   * A revealed cell carries the ground under a relic, not the relic: the
   * state draws its markers over the terrain from the whole seen set, and a
   * move that sent only the tile stepped on left a relic seen from two tiles
   * away drawn as forest until a reload. Only tiles in the reveal set, so
   * nothing already known is repeated.
   */
  relics: z.array(relicMarkerSchema),
  log: logLineSchema,
  /** True when this step left no legal move behind it (Q20.2). */
  spent: z.boolean(),
  /** A fragment the ruin gave up, by catalogue id. */
  fragmentId: z.string().optional(),
  /** The relic tile stepped on, if there was one. */
  relic: relicMarkerSchema.optional(),
  /**
   * Deliveries this step caught out: the player walked a tile and found it
   * different from what a charted letter claimed. Inbox public ids, never
   * senders (`docs/anonymity.md` R1).
   */
  contradicted: z.array(z.string()),
})
export type ApiMoveResponse = z.infer<typeof moveResponseSchema>
